import { Box, Grid2, Typography } from "@mui/material";
import {
  DEFAULT_TEXT_FONT_SIZE,
  DEFAULT_TITLE_FONT_SIZE,
} from "@styles/common-styles";
import {
  PageSectionItem,
  PageSectionItemData,
} from "@components/page-section/page-section-item";
import { neutral } from "@flat-ui";
import { ReactNode } from "react";
import { PageSectionInfoIcon } from "@components/page-section/page-section-info-icon";
import { useMediaQueryList } from "@hooks/use-media-query";

export interface PageSectionProps {
  /** The title of the section, shown above the subtitle on the left side of the screen. */
  title: ReactNode;

  /** Additional information about the section to displayed in bold text */
  additionalInfo?: string;

  /** An optional text that shows as a tooltip on hovering an info icon that will be shown if tooltipInfo is passed */
  tooltipInfo?: ReactNode;

  /** The subtitle of the section, shown below the title on the left side of the screen. */
  subtitle: ReactNode;

  /** The items in the section */
  items: PageSectionItemData[];

  /**
   * Optional flag when the content is loading,
   * if true, the content in all items will be replaced with a skeleton.
   */
  isLoading?: boolean;

  /**
   * Optional flag to indicate if this section should be hidden.
   * If true, the section will be hidden and it won't take any space.
   */
  isHidden?: boolean;

  /**
   * Optional flag to indicate if this is the top section, in that case,
   * the top margin will be different and it won't have a top border.
   */
  isTopSection?: boolean;

  /**
   * Optional flag to indicate if this is the last section, in that case,
   * the bottom margin will be different.
   */
  isLastSection?: boolean;

  /**
   * Optional element to be displayed at the bottom of the section.
   * If provided, it will be positioned below all items in the section.
   */
  footerAction?: ReactNode;

  /** Optional flag to indicate if we should hide the left column */
  shouldHideLeftColumn?: boolean;

  /** Optional flag to indicate if there is a section description for this tab */
  sectionDescription?: ReactNode;
}

/**
 * Defines a page section that can be used in pages like account or settings,
 * includes a title, subtitle and a list of items.
 */
export function PageSection({
  title,
  additionalInfo,
  tooltipInfo,
  subtitle,
  items,
  isLoading = false,
  isTopSection = false,
  isLastSection = false,
  footerAction,
  shouldHideLeftColumn = false,
  sectionDescription,
}: PageSectionProps): JSX.Element {
  const { isScreenSmOrSmaller } = useMediaQueryList();

  return (
    // Wrapper for the whole section, usually shown as a row in the page
    <Grid2
      container
      alignItems="baseline"
      // Since the sections in smaller screens are stacked, we add margin bottom to separate them
      marginBottom={isScreenSmOrSmaller && !isLastSection ? "20px" : "0px"}
    >
      {sectionDescription}

      {/* Left column with title and subtitle */}
      {!shouldHideLeftColumn && (
        <Grid2
          sx={{
            paddingRight: {
              xs: "0px",
              md: "60px",
            },
            paddingBottom: {
              xs: "30px",
              md: "0px",
            },
          }}
          size={{
            xs: 12,
            sm: 12,
            md: 12,
            lg: 4,
          }}
        >
          {/* Title and subtitle wrapper */}
          <Box
            sx={{
              marginTop: {
                md: isTopSection ? "24px" : "75px",
              },
            }}
          >
            {/* Title of the section */}
            <Box
              sx={{
                fontSize: DEFAULT_TITLE_FONT_SIZE,
                letterSpacing: "-0.16px",
                fontWeight: 600,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>{title}</Box>
              {tooltipInfo && <PageSectionInfoIcon text={tooltipInfo} />}
            </Box>

            {/* Additional Information section */}
            {additionalInfo && (
              <Typography
                sx={{
                  color: neutral[600],
                  fontSize: "10px",
                  fontWeight: "bold",
                  textTransform: "uppercase",
                }}
              >
                {additionalInfo}
              </Typography>
            )}

            {/* Subtitle of the section */}
            <Box
              sx={{
                fontSize: DEFAULT_TEXT_FONT_SIZE,
                marginTop: "10px",
              }}
            >
              {subtitle}
            </Box>
          </Box>

          {footerAction && <Box pt={"1rem"}>{footerAction}</Box>}
        </Grid2>
      )}
      {/* Right column with the items */}
      <Grid2
        sx={{
          marginTop: {
            md: isTopSection ? "0px" : "75px",
          },
        }}
        size={
          shouldHideLeftColumn
            ? // eslint-disable-next-line @typescript-eslint/no-magic-numbers
              12
            : {
                xs: 12,
                sm: 12,
                md: 12,
                lg: 8,
              }
        }
      >
        {items
          .filter((item) => !item.isHidden)
          .map((item, index) => (
            <PageSectionItem
              item={item}
              key={`page-subsection-${index}`}
              isLoading={isLoading}
              shouldShowTopBorder={
                index === 0 && !isTopSection && !isScreenSmOrSmaller
              }
            />
          ))}
      </Grid2>
    </Grid2>
  );
}
